import { useEffect, useId, useRef, useState } from 'react';
import type { TipoFranja } from '../logica/franjas';
import { ESTILO_FRANJA as ESTILO } from './estiloFranja';
import { Icono } from './Icono';

/**
 * RV Franja (componentes.md §15), variante `compacta` que usa el marco 0.1: una línea de 48 (radio 14) con la baldosa del
 * icono (32), el título con «…» (el corto en móvil), la acción en píldora blanca de 32 (zona 44) y la X de §15b si es
 * cerrable. El texto largo va en el bocadillo (`title`) y para el lector (`aria-describedby`). Colores: los de la pieza
 * del diseño (`RV_FRANJA`): índigo `--rv-soporte` para lo que ve la persona de Reservaria (suplantación y Soporte, 0.5),
 * rojo para solo lectura e impago, negro para «Soporte está en tu negocio» y amarillo para prueba, baja y cupo. Al cerrar,
 * se pliega (360 ms) llevándose su espacio y después llama a `onCerrar`.
 */
interface Props {
    tipo: TipoFranja;
    titulo: string;
    tituloCorto?: string;
    texto?: string;
    accion?: string | null;
    onAccion?: () => void;
    movil?: boolean;
    cerrable?: boolean;
    etiquetaCerrar?: string;
    onCerrar?: () => void;
    /** Px de aire debajo, que se pliega con ella. */
    espacio?: number;
}

export function Franja({ tipo, titulo, tituloCorto, texto = '', accion, onAccion, movil = false, cerrable = false, etiquetaCerrar = 'Cerrar este aviso', onCerrar, espacio = 0 }: Props) {
    const e = ESTILO[tipo];
    const oscura = e.tinta === '#fff';
    const [cerrando, setCerrando] = useState(false);
    const reloj = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const idTexto = useId();
    useEffect(() => () => {
        clearTimeout(reloj.current);
    }, []);

    const fondoX = oscura ? 'rgba(255,255,255,0.14)' : 'rgba(17,17,17,0.08)';
    const hoverX = oscura ? 'rgba(255,255,255,0.26)' : 'rgba(17,17,17,0.16)';
    const hayAccion = accion != null && accion !== '';

    return (
        <div className="rv-franja-pliega" style={{ gridTemplateRows: cerrando ? '0fr' : '1fr', opacity: cerrando ? 0 : 1 }}>
            <div style={{ minHeight: 0, overflow: cerrando ? 'hidden' : 'visible' }}>
                <div style={{ paddingBottom: espacio }}>
                    <div role="status" title={texto} className="rv-franja-c" style={{ background: e.fondo, color: e.tinta, paddingRight: cerrable ? 2 : 8 }}>
                        <span className="rv-franja-c__baldosa" style={{ background: e.baldosa }}>
                            <Icono nombre={e.icono} />
                        </span>
                        <span className="rv-franja-c__titulo" aria-describedby={idTexto}>
                            {movil && tituloCorto !== undefined ? tituloCorto : titulo}
                        </span>
                        <span id={idTexto} className="rv-sr">
                            {texto}
                        </span>
                        {hayAccion && (
                            <button type="button" className="rv-franja-c-btn" onClick={onAccion}>
                                <span className="rv-franja-c__accion">
                                    {accion}
                                    <Icono nombre="arrow-right" />
                                </span>
                            </button>
                        )}
                        {cerrable && (
                            <button
                                type="button"
                                className="rv-cerrar"
                                aria-label={etiquetaCerrar}
                                style={{ ['--rv-cerrar-hover' as string]: hoverX }}
                                onClick={() => {
                                    if (cerrando) {
                                        return;
                                    }
                                    setCerrando(true);
                                    reloj.current = setTimeout(() => onCerrar?.(), 360);
                                }}
                            >
                                <span style={{ background: fondoX }}>
                                    <Icono nombre="x" />
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
